<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import ReviewCard from '~/components/cards/ReviewCard.vue'
import BasicButton from '~/components/form-elements/BasicButton.vue'
import Auth from '~/components/modal-windows/Auth.vue'
import ConfirmDeletion from '~/components/modal-windows/ConfirmDeletion.vue'
import CreateOrEditReview from '~/components/modal-windows/CreateOrEditReview.vue'
import { useWindowHelpers } from '~/composables/window-helpers'
import constants from '~/constants'
import AuthAction from '~/enums/auth-action'
import helpers from '~/helpers'
import { useAuthStore } from '~/store/auth'
import type { Page } from '~/types/json-api'

const { $apiHelper, $toast } = useNuxtApp()
const authStore = useAuthStore()
const { scrollTo } = useWindowHelpers()

const messages = {
  failedToDeleteReview: 'Не удалось удалить отзыв.',
  failedToGetReviews: 'Не удалось получить отзывы.',
  noReviews: 'Нет отзывов о товаре.',
  reviewIsDeleted: 'Отзыв удалён.'
}

const props = defineProps<{
  isProductPurchasedByUser: boolean | null
  productId: string
  userReviewId: string | null
}>()

const emit = defineEmits<{
  (e: 'setTotalPublishedReviews', x: number): void
  (e: 'setUserReviewId', x: string | null): void
}>()

const page = ref<Page>({ number: 1, size: 10 })
const totalPagesOfReviews = ref<number | null>(null)

const { data: reviewsData, status: reviewsStatus } = await useLazyAsyncData('reviewsDataKey', loadReviewsData)

async function changePageOfReviews (x: number): Promise<void> {
  scrollTo('scroll-anchor', 84)

  page.value.number = x

  await refreshNuxtData('reviewsDataKey')
}

async function deleteReview (id: string): Promise<void> {
  const { failedToDeleteReview, reviewIsDeleted } = messages

  try {
    await $apiHelper.reviews.deleteReview(id)

    setUserReviewId(null)

    $toast.success(reviewIsDeleted)
  } catch (error) {
    console.error(error)

    $toast.error(failedToDeleteReview)
  }
}

async function loadReviewsData (): Promise<any> {
  const params = helpers.getParams([
    { name: 'filter[productId]', value: props.productId, condition: true },
    { name: 'page[number]', value: page.value.number, condition: true },
    { name: 'page[size]', value: page.value.size, condition: true }
  ])

  try {
    const { data, meta } = await $apiHelper.reviews.getReviews(params) as any
    const { totalReviews } = meta

    emit('setTotalPublishedReviews', totalReviews)

    totalPagesOfReviews.value = helpers.getTotalPages(totalReviews, page.value.size)

    return data
  } catch (error) {
    console.error(error)

    $toast.error(messages.failedToGetReviews)

    throw error
  }
}

async function openAuthModalWindow (): Promise<void> {
  await useModal({ component: Auth, attrs: { action: AuthAction.SignIn } }).open()
}

async function openConfirmDeletionModalWindow (): Promise<void> {
  await useModal({
    component: ConfirmDeletion,
    attrs: {
      id: props.userReviewId,
      text: constants.confirmDeletionTexts.review,
      onConfirmDeletion: deleteReview
    }
  }).open()
}

async function openCreateOrEditReviewModalWindow (): Promise<void> {
  const { productId, userReviewId } = props

  await useModal({
    component: CreateOrEditReview,
    attrs: { productId, reviewId: userReviewId, onSetUserReviewId: setUserReviewId }
  }).open()
}

function setUserReviewId (x: string | null): void {
  emit('setUserReviewId', x)
}
</script>

<template>
  <div>
    <div v-if="authStore.isUser" class="mb-6">
      <template v-if="isProductPurchasedByUser">
        <BasicButton
          color="green"
          is-small
          :title="userReviewId === null ? 'Написать отзыв' : 'Изменить отзыв'"
          @click="openCreateOrEditReviewModalWindow"
        />

        <BasicButton
          v-if="userReviewId !== null"
          class="mt-6"
          color="red"
          is-small
          title="Удалить отзыв"
          @click="openConfirmDeletionModalWindow"
        />
      </template>
      <Notification v-else status="info">
        Чтобы написать отзыв, необходимо приобрести товар.
      </Notification>
    </div>
    <Notification v-else class="mb-6" status="info">
      Чтобы написать отзыв, <a
        href="#"
        @click.prevent="openAuthModalWindow"
      >войдите или зарегистрируйтесь</a>.
    </Notification>

    <div id="scroll-anchor" />

    <Preloader v-if="reviewsStatus === 'pending'" />
    <template v-else-if="reviewsStatus === 'success' && reviewsData">
      <template v-if="reviewsData.length > 0">
        <ReviewCard v-for="x in reviewsData" :key="x.id" class="mb-6" :review-data="x" />
      </template>
      <Notification v-else status="info">
        {{ messages.noReviews }}
      </Notification>
    </template>
    <template v-else>
      <Notification class="mb-4" status="error">
        {{ messages.failedToGetReviews }}
      </Notification>

      <a href="#" @click.prevent="refreshNuxtData('reviewsDataKey')">Повторить попытку</a>
    </template>

    <Pagination :current-page="page.number" :last-page="totalPagesOfReviews" @change-page="changePageOfReviews" />
  </div>
</template>
